import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getListVenuesByUser } from '../api';
import {
  FAILED, IDLE, PENDING, SUCCEEDED,
} from '../constants/asyncLoadingStatuses';
import { getMultipleListVenues, patchVenueListsThunk } from './favoriteSlice';
import { getListDetailContext } from '../utils/getPageContext';
import handlePayloadCreator from './helpers/handlePayloadCreator';

const getListVenuesByUserThunk = createAsyncThunk(
  'favoriteVenues/getListVenuesByUser', handlePayloadCreator(getListVenuesByUser)
);

const favoriteVenuesSlice = createSlice({
  name: 'favoriteVenues',
  initialState: {
    fetchStatus: IDLE,
    venuesByUser: {},
    venuesByList: {},
  },
  reducers: {
    updateVenuesByList: (state, action) => {
      const { venuesByList } = action.payload;
      state.venuesByList = venuesByList;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getListVenuesByUserThunk.pending, (state) => {
      state.fetchStatus = PENDING;
    });
    builder.addCase(getListVenuesByUserThunk.fulfilled, (state, action) => {
      const { results: { venue_ids: venues = [] } } = action.payload;
      const venuesByUser = {};
      venues.forEach((id) => {
        venuesByUser[id] = { id };
      });
      state.venuesByUser = venuesByUser;
      state.fetchStatus = SUCCEEDED;
    });
    builder.addCase(getListVenuesByUserThunk.rejected, (state) => {
      state.fetchStatus = FAILED;
    });
    builder.addMatcher(
      (action) => [
        'favorite/getListVenues/fulfilled',
      ].includes(action.type),
      (state, action) => {
        /* istanbul ignore next */
        const { results: { venues = [] } } = action.payload;
        const idList = venues.map((venue) => venue?.id?.resy);
        const venuesByList = {};
        idList.forEach((id) => {
          venuesByList[id] = { id };
        });
        state.fetchStatus = SUCCEEDED;
        state.venuesByList = venuesByList;
      }
    );
    builder.addMatcher(
      (action) => action.type === 'authToken/removeAuthToken',
      (state) => {
        state.fetchStatus = IDLE;
        state.venuesByList = {};
        state.venuesByUser = {};
      }
    );
  },
});

const { updateVenuesByList } = favoriteVenuesSlice.actions;

// List Detail Page context is special case when removing / unfavoriting a venue:
// Here the favorite state must correspond to the page's SPECIFIC list,
// whereas elsewhere on the site, favorite state refers to being included on ANY list.
const handleListDetailContextThunk = (params) => (dispatch, getState) => {
  const { listIdFromUrl, listIdsForVenue } = params;
  const { favorite } = getState();
  const venuesForListId = favorite?.listById[listIdFromUrl]?.results?.venues;
  const isRemoved = !listIdsForVenue?.includes(listIdFromUrl);
  const limit = (isRemoved) ? (venuesForListId.length - 1) : (venuesForListId.length + 1);
  // Call to update list total on list detail page
  return dispatch(getMultipleListVenues({ listId: listIdFromUrl, limit, offset: 0 }))
    .then(({ payload }) => {
      const { results: { venues } } = payload;
      const idList = venues.map((venue) => venue.id?.resy);
      const venuesByList = {};
      idList.forEach((id) => {
        venuesByList[id] = { id };
      });
      dispatch(updateVenuesByList({ venuesByList }));
      dispatch(getListVenuesByUserThunk());
    });
};

const addVenueToListsThunk = (params) => (dispatch) => dispatch(patchVenueListsThunk(params))
  .then((response) => {
    const { list_ids: listIdsForVenue } = response.payload;
    const listIdFromUrl = getListDetailContext(window.location.pathname);

    if (listIdFromUrl) {
      return dispatch(handleListDetailContextThunk({ listIdFromUrl, listIdsForVenue }));
    }
    return dispatch(getListVenuesByUserThunk());
  });

export {
  addVenueToListsThunk,
  getListVenuesByUserThunk,
};
export default favoriteVenuesSlice.reducer;
